import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  CreateListDto,
  CreateListItemDto,
  List,
  ListItem,
  ListWithItems,
  ReorderListsDto,
  UpdateListDto,
  UpdateListItemDto,
} from '@mrrmlab/shared-types';
import type { TrainingStats } from '@mrrmlab/api-client';
import { useApiClient, useOnSourceChange } from './api-client-context';
import { listKeys } from './query-keys';

export function useTrainingStats(
  options?: Omit<UseQueryOptions<TrainingStats>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<TrainingStats>({
    queryKey: listKeys.trainingStats(),
    queryFn: ({ signal }) => client.lists.trainingStats(signal),
    ...options,
  });
}

export function useLists(
  options?: Omit<UseQueryOptions<List[]>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<List[]>({
    queryKey: listKeys.lists(),
    queryFn: ({ signal }) => client.lists.list(signal),
    ...options,
  });
}

export function useList(
  id: string,
  options?: Omit<UseQueryOptions<ListWithItems>, 'queryKey' | 'queryFn'>,
) {
  const client = useApiClient();
  return useQuery<ListWithItems>({
    queryKey: listKeys.detail(id),
    queryFn: ({ signal }) => client.lists.get(id, signal),
    ...options,
  });
}

type MutationExtras<TData, TVars> = Omit<
  UseMutationOptions<TData, Error, TVars>,
  'mutationFn'
>;

export function useCreateList(options?: MutationExtras<List, CreateListDto>) {
  const client = useApiClient();
  const qc = useQueryClient();
  const onSourceChange = useOnSourceChange();
  return useMutation<List, Error, CreateListDto>({
    mutationFn: (dto) => client.lists.create(dto),
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: listKeys.lists() });
      onSourceChange({ sourceType: 'list', sourceId: data.id, kind: 'created' });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateList(
  options?: MutationExtras<List, { id: string; dto: UpdateListDto }>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  const onSourceChange = useOnSourceChange();
  return useMutation<List, Error, { id: string; dto: UpdateListDto }>({
    mutationFn: ({ id, dto }) => client.lists.update(id, dto),
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: listKeys.lists() });
      qc.invalidateQueries({ queryKey: listKeys.detail(vars.id) });
      onSourceChange({ sourceType: 'list', sourceId: vars.id, kind: 'updated' });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useReorderLists() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, ReorderListsDto, { previous: List[] | undefined }>({
    mutationFn: (dto) => client.lists.reorder(dto),
    onMutate: async (dto) => {
      await qc.cancelQueries({ queryKey: listKeys.lists() });
      const previous = qc.getQueryData<List[]>(listKeys.lists());
      if (previous) {
        const byId = new Map(previous.map((l) => [l.id, l] as const));
        const reordered = dto.orderedIds
          .map((id, idx) => {
            const l = byId.get(id);
            return l ? { ...l, position: idx } : null;
          })
          .filter((l): l is List => l !== null);
        const missing = previous.filter((l) => !dto.orderedIds.includes(l.id));
        qc.setQueryData<List[]>(listKeys.lists(), [...reordered, ...missing]);
      }
      return { previous };
    },
    onError: (_err, _vars, onMutateResult) => {
      if (onMutateResult?.previous) qc.setQueryData(listKeys.lists(), onMutateResult.previous);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: listKeys.lists() });
    },
  });
}

export function useDeleteList(options?: MutationExtras<void, string>) {
  const client = useApiClient();
  const qc = useQueryClient();
  const onSourceChange = useOnSourceChange();
  return useMutation<void, Error, string>({
    mutationFn: (id) => client.lists.delete(id),
    ...options,
    onSuccess: (data, id, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: listKeys.lists() });
      qc.removeQueries({ queryKey: listKeys.detail(id) });
      onSourceChange({ sourceType: 'list', sourceId: id, kind: 'deleted' });
      options?.onSuccess?.(data, id, onMutateResult, ctx);
    },
  });
}

export function useAddListItem(
  listId: string,
  options?: MutationExtras<ListItem, CreateListItemDto>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  const onSourceChange = useOnSourceChange();
  return useMutation<ListItem, Error, CreateListItemDto>({
    mutationFn: (dto) => client.lists.items.add(listId, dto),
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: listKeys.detail(listId) });
      onSourceChange({ sourceType: 'list', sourceId: listId, kind: 'updated' });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUpdateListItem(
  listId: string,
  options?: MutationExtras<ListItem, { itemId: string; dto: UpdateListItemDto }>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  const onSourceChange = useOnSourceChange();
  return useMutation<ListItem, Error, { itemId: string; dto: UpdateListItemDto }>({
    mutationFn: ({ itemId, dto }) => client.lists.items.update(listId, itemId, dto),
    ...options,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: listKeys.detail(listId) });
      onSourceChange({ sourceType: 'list', sourceId: listId, kind: 'updated' });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useDeleteListItem(
  listId: string,
  options?: MutationExtras<void, string>,
) {
  const client = useApiClient();
  const qc = useQueryClient();
  const onSourceChange = useOnSourceChange();
  return useMutation<void, Error, string>({
    mutationFn: (itemId) => client.lists.items.delete(listId, itemId),
    ...options,
    onSuccess: (data, itemId, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: listKeys.detail(listId) });
      onSourceChange({ sourceType: 'list', sourceId: listId, kind: 'updated' });
      options?.onSuccess?.(data, itemId, onMutateResult, ctx);
    },
  });
}
